import React, { useState, useEffect, useMemo } from 'react';
import Cookies from 'js-cookie';

import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockIcon from '@mui/icons-material/Lock';
import PermissionsPanel from '../../components/PermissionsPanel';
import { checkPermission } from '../../utils/auth';

const API_BASE = 'https://system.sales-management.cloud/api/users.php';

function AdminUserManagement() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [allUsers, setAllUsers] = useState([]);
    const [orgLoading, setOrgLoading] = useState(false);
    const [showPermissionsPanel, setShowPermissionsPanel] = useState(false);
    const [selectedUserForPermissions, setSelectedUserForPermissions] = useState(null);
    const [permissionLoading, setPermissionLoading] = useState(true);
    const [operationPermissions, setOperationPermissions] = useState({
        canCreateUsers: false,
        canEditUsers: false,
        canDeleteUsers: false,
        canManagePermissions: false
    });
    const pageSize = 20;

    // フィルター状態
    const [filters, setFilters] = useState({
        search: '',
        role: '',
        status: '',
        department: ''
    });

    // フォーム状態
    const [formData, setFormData] = useState({
        name: '',
        user_id: '',
        role: 'user',
        status: 'active',
        department: '',
        team_id: '',
        supervisor_id: '',
        emoji: '',
        color: ''
    });

    // ステータスとロールの定義
    const roleOptions = [
        { value: 'superadmin', label: 'スーパー管理者' },
        { value: 'admin', label: '管理者' },
        { value: 'view_admin', label: '閲覧管理者' },
        { value: 'contractor', label: '契約者' },
        { value: 'user', label: 'ユーザー' }
    ];

    const statusOptions = [
        { value: 'active', label: 'アクティブ', icon: CheckCircleIcon, color: 'bg-green-100 text-green-800' },
        { value: 'inactive', label: '非アクティブ', icon: CancelIcon, color: 'bg-gray-100 text-gray-800' },
        { value: 'suspended', label: '停止中', icon: CancelIcon, color: 'bg-red-100 text-red-800' }
    ];

    // ユーザー一覧を取得
    const fetchUsers = async (page = 1) => {
        try {
            setLoading(true);
            setError('');
            const token = Cookies.get('auth_token');
            if (!token) {
                setError('認証情報が見つかりません');
                return;
            }

            const params = new URLSearchParams({
                limit: pageSize,
                offset: (page - 1) * pageSize,
                sort_by: 'name',
                sort_order: 'asc',
                include_team: '1'
            });

            if (filters.search) params.append('search', filters.search);
            if (filters.role) params.append('role', filters.role);
            if (filters.status) params.append('status', filters.status);
            if (filters.department) params.append('department', filters.department);

            const response = await fetch(`${API_BASE}?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            
            if (data.status === 'success') {
                setUsers(data.data.users || []);
                setTotalCount(data.data.total || 0);
                setCurrentPage(page);
            } else {
                setError(data.message || 'ユーザー一覧の取得に失敗しました');
            }
        } catch (err) {
            console.error('Fetch error:', err);
            setError('通信エラーが発生しました');
        } finally {
            setLoading(false);
        }
    };

    const fetchAllUsersForOrg = async () => {
        try {
            setOrgLoading(true);
            const token = Cookies.get('auth_token');
            if (!token) {
                return;
            }

            const mergedUsers = [];
            const limit = 100;
            let offset = 0;
            let total = 0;

            do {
                const params = new URLSearchParams({
                    limit: String(limit),
                    offset: String(offset),
                    sort_by: 'name',
                    sort_order: 'asc'
                });

                const response = await fetch(`${API_BASE}?${params.toString()}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                const data = await response.json();
                if (data.status !== 'success') {
                    throw new Error(data.message || '組織図データの取得に失敗しました');
                }

                const chunk = data.data?.users || [];
                total = Number(data.data?.total || 0);
                mergedUsers.push(...chunk);
                offset += chunk.length;

                if (chunk.length === 0) {
                    break;
                }
            } while (mergedUsers.length < total);

            setAllUsers(mergedUsers);
        } catch (err) {
            console.error('Fetch org users error:', err);
            setError('組織図データの取得に失敗しました');
        } finally {
            setOrgLoading(false);
        }
    };

    // 初期ロード
    useEffect(() => {
        fetchUsers(1);
        fetchAllUsersForOrg();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const loadOperationPermissions = async () => {
            try {
                setPermissionLoading(true);
                const [canCreateUsers, canEditUsers, canDeleteUsers, canManagePermissions] = await Promise.all([
                    checkPermission('users', 'create'),
                    checkPermission('users', 'edit'),
                    checkPermission('users', 'delete'),
                    checkPermission('users', 'manage_permissions')
                ]);

                setOperationPermissions({
                    canCreateUsers,
                    canEditUsers,
                    canDeleteUsers,
                    canManagePermissions
                });
            } catch (err) {
                console.error('Failed to load operation permissions:', err);
            } finally {
                setPermissionLoading(false);
            }
        };

        loadOperationPermissions();
    }, []);

    // フィルター変更時
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchUsers(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

    // フィルター入力の処理
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // フォーム入力の処理
    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // ユーザー編集を開く
    const openEditModal = (user) => {
        if (!operationPermissions.canEditUsers) {
            setError('ユーザー編集権限がありません');
            return;
        }

        setSelectedUser(user);
        setFormData({
            name: user.name,
            user_id: user.user_id,
            role: user.role,
            status: user.status,
            department: user.department || '',
            team_id: user.team_id || '',
            supervisor_id: user.supervisor_id ? String(user.supervisor_id) : '',
            emoji: user.emoji || '',
            color: user.color || ''
        });
        setIsEditing(true);
        setShowModal(true);
    };

    // 権限管理パネルを開く
    const openPermissionsPanel = (user) => {
        if (!operationPermissions.canManagePermissions) {
            setError('権限管理の実行権限がありません');
            return;
        }

        setSelectedUserForPermissions(user);
        setShowPermissionsPanel(true);
    };

    // 権限管理パネルを閉じる
    // eslint-disable-next-line no-unused-vars
    const closePermissionsPanel = () => {
        setShowPermissionsPanel(false);
        setSelectedUserForPermissions(null);
    };

    // 新規ユーザーモーダルを開く
    const openCreateModal = () => {
        if (!operationPermissions.canCreateUsers) {
            setError('ユーザー作成権限がありません');
            return;
        }

        setFormData({
            name: '',
            user_id: '',
            role: 'user',
            status: 'active',
            department: '',
            team_id: '',
            supervisor_id: '',
            emoji: '',
            color: ''
        });
        setIsEditing(false);
        setShowModal(true);
    };

    // モーダルを閉じる
    const closeModal = () => {
        setShowModal(false);
        setError('');
    };

    // フォーム送信
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setError('');

            if (isEditing && !operationPermissions.canEditUsers) {
                setError('ユーザー編集権限がありません');
                return;
            }

            if (!isEditing && !operationPermissions.canCreateUsers) {
                setError('ユーザー作成権限がありません');
                return;
            }

            const token = Cookies.get('auth_token');
            if (!token) {
                setError('認証情報が見つかりません');
                return;
            }

            // バリデーション
            if (!formData.name.trim() || !formData.user_id.trim()) {
                setError('必須項目を入力してください');
                return;
            }

            const payload = {
                ...formData,
                team_id: formData.team_id === '' ? null : Number(formData.team_id),
                supervisor_id: formData.supervisor_id === '' ? null : Number(formData.supervisor_id)
            };

            if (isEditing && payload.supervisor_id === selectedUser.id) {
                setError('自分自身を上司には設定できません');
                return;
            }

            let response;
            if (isEditing) {
                // PUT: ユーザー更新
                response = await fetch(`${API_BASE}?id=${selectedUser.id}`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });
            } else {
                // POST: 新規ユーザー作成
                response = await fetch(API_BASE, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });
            }

            const data = await response.json();
            
            if (data.status === 'success') {
                setSuccess(isEditing ? 'ユーザーを更新しました' : 'ユーザーを作成しました');
                closeModal();
                fetchUsers(currentPage);
                fetchAllUsersForOrg();
            } else {
                setError(data.message || 'エラーが発生しました');
            }
        } catch (err) {
            console.error('Submit error:', err);
            setError('通信エラーが発生しました');
        }
    };

    // ユーザー削除
    const handleDeleteUser = async (userId) => {
        if (!operationPermissions.canDeleteUsers) {
            setError('ユーザー削除権限がありません');
            return;
        }

        if (!window.confirm('このユーザーを削除してもよろしいですか？')) {
            return;
        }

        try {
            setError('');
            const token = Cookies.get('auth_token');
            if (!token) {
                setError('認証情報が見つかりません');
                return;
            }

            const response = await fetch(`${API_BASE}?id=${userId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            
            if (data.status === 'success') {
                setSuccess('ユーザーを削除しました');
                fetchUsers(currentPage);
                fetchAllUsersForOrg();
            } else {
                setError(data.message || '削除に失敗しました');
            }
        } catch (err) {
            console.error('Delete error:', err);
            setError('通信エラーが発生しました');
        }
    };

    // ページネーション
    const totalPages = Math.ceil(totalCount / pageSize);

    const getRoleBadgeColor = (role) => {
        const colors = {
            superadmin: 'bg-red-100 text-red-800',
            admin: 'bg-purple-100 text-purple-800',
            view_admin: 'bg-blue-100 text-blue-800',
            contractor: 'bg-yellow-100 text-yellow-800',
            user: 'bg-gray-100 text-gray-800'
        };
        return colors[role] || 'bg-gray-100 text-gray-800';
    };

    const getStatusBadgeColor = (status) => {
        const colors = {
            active: 'bg-green-100 text-green-800',
            inactive: 'bg-gray-100 text-gray-800',
            suspended: 'bg-red-100 text-red-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const getStatusLabel = (status) => {
        const labels = {
            active: 'アクティブ',
            inactive: '非アクティブ',
            suspended: '停止中'
        };
        return labels[status] || status;
    };

    const getRoleLabel = (role) => {
        const option = roleOptions.find(opt => opt.value === role);
        return option ? option.label : role;
    };

    const supervisorOptions = useMemo(() => {
        return [...allUsers]
            .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'ja'));
    }, [allUsers]);

    const orgRoots = useMemo(() => {
        const nodesById = new Map();
        allUsers.forEach((user) => {
            nodesById.set(user.id, {
                ...user,
                children: []
            });
        });

        const roots = [];
        nodesById.forEach((node) => {
            const supervisorId = node.supervisor_id ? Number(node.supervisor_id) : null;
            if (supervisorId && supervisorId !== node.id && nodesById.has(supervisorId)) {
                nodesById.get(supervisorId).children.push(node);
            } else {
                roots.push(node);
            }
        });

        const sortNodes = (nodes) => {
            nodes.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'ja'));
            nodes.forEach((child) => sortNodes(child.children));
        };

        sortNodes(roots);
        return roots;
    }, [allUsers]);

    const renderOrgNode = (node, depth = 0, ancestorIds = []) => {
        const cycleDetected = ancestorIds.includes(node.id);
        const nextAncestorIds = [...ancestorIds, node.id];

        return (
            <div key={node.id} className="space-y-2">
                <div
                    className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2"
                    style={{ marginLeft: `${depth * 20}px` }}
                >
                    {node.emoji && <span className="text-lg">{node.emoji}</span>}
                    <span className="font-medium text-gray-900">{node.name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(node.role)}`}>
                        {getRoleLabel(node.role)}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(node.status)}`}>
                        {getStatusLabel(node.status)}
                    </span>
                </div>
                {cycleDetected ? (
                    <div className="text-xs text-red-600" style={{ marginLeft: `${(depth + 1) * 20}px` }}>
                        階層ループを検出したため表示を停止しました
                    </div>
                ) : (
                    node.children.map((child) => renderOrgNode(child, depth + 1, nextAncestorIds))
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
            <div className="max-w-7xl mx-auto">
                {/* ヘッダー */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="bg-blue-500 rounded-lg p-3">
                                <PersonIcon className="text-white" style={{ fontSize: '32px' }} />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-800">ユーザー管理</h1>
                                <p className="text-gray-600">ユーザー情報の閲覧、編集、管理を行います</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => {
                                    fetchUsers(currentPage);
                                    fetchAllUsersForOrg();
                                }}
                                className="flex items-center gap-2 border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 px-4 py-2 rounded-lg transition"
                            >
                                <RefreshIcon />
                                更新
                            </button>
                            <button
                                onClick={openCreateModal}
                                disabled={permissionLoading || !operationPermissions.canCreateUsers}
                                className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition"
                                title={operationPermissions.canCreateUsers ? '新規ユーザーを作成' : 'ユーザー作成権限がありません'}
                            >
                                <PersonAddIcon />
                                新規ユーザー
                            </button>
                        </div>
                    </div>

                    {!permissionLoading && !operationPermissions.canManagePermissions && (
                        <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
                            権限管理は実行権限があるユーザーのみ利用できます。
                        </div>
                    )}
                </div>

                {/* エラー・成功メッセージ */}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
                        {success}
                    </div>
                )}

                {/* フィルターセクション */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <div className="mb-4 flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-800">フィルター</h2>
                        <button
                            type="button"
                            onClick={() => setFilters({ search: '', role: '', status: '', department: '' })}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                        >
                            クリア
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">検索</label>
                            <div className="relative">
                                <SearchIcon className="absolute left-3 top-3 text-gray-400" />
                                <input
                                    type="text"
                                    name="search"
                                    placeholder="名前"
                                    value={filters.search}
                                    onChange={handleFilterChange}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">ロール</label>
                            <select
                                name="role"
                                value={filters.role}
                                onChange={handleFilterChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">すべて</option>
                                {roleOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">ステータス</label>
                            <select
                                name="status"
                                value={filters.status}
                                onChange={handleFilterChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">すべて</option>
                                {statusOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">部署</label>
                            <input
                                type="text"
                                name="department"
                                placeholder="部署名"
                                value={filters.department}
                                onChange={handleFilterChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-800">組織図（上司-部下）</h2>
                        <button
                            type="button"
                            onClick={fetchAllUsersForOrg}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                        >
                            更新
                        </button>
                    </div>

                    {orgLoading ? (
                        <p className="text-sm text-gray-600">組織図を読み込み中...</p>
                    ) : orgRoots.length === 0 ? (
                        <p className="text-sm text-gray-600">組織図データがありません</p>
                    ) : (
                        <div className="space-y-3 max-h-96 overflow-auto pr-2">
                            {orgRoots.map((root) => renderOrgNode(root))}
                        </div>
                    )}
                </div>

                {/* ユーザー一覧 */}
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center">
                            <div className="inline-block animate-spin">
                                <RefreshIcon className="text-blue-500" style={{ fontSize: '40px' }} />
                            </div>
                            <p className="text-gray-600 mt-4">読み込み中...</p>
                        </div>
                    ) : users.length === 0 ? (
                        <div className="p-8 text-center">
                            <PersonIcon className="text-gray-400 mx-auto mb-4" style={{ fontSize: '48px' }} />
                            <p className="text-gray-600">ユーザーが見つかりません</p>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">名前</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">ユーザーID</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">ロール</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">部署</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">上司</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">ステータス</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">操作</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {users.map(user => (
                                            <tr key={user.id} className="hover:bg-gray-50 transition">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        {user.emoji && <span className="text-2xl">{user.emoji}</span>}
                                                        <span className="font-medium text-gray-900">{user.name}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-gray-600 text-sm">{user.user_id}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                                                        {getRoleLabel(user.role)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-gray-600 text-sm">{user.department || '-'}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-gray-600 text-sm">{user.supervisor_name || '-'}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(user.status)}`}>
                                                        {getStatusLabel(user.status)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => openEditModal(user)}
                                                            disabled={permissionLoading || !operationPermissions.canEditUsers}
                                                            className={`p-2 ${operationPermissions.canEditUsers ? 'text-blue-500 hover:text-blue-700' : 'text-gray-300 cursor-not-allowed'}`}
                                                            title={operationPermissions.canEditUsers ? '編集' : '編集権限がありません'}
                                                        >
                                                            <EditIcon />
                                                        </button>
                                                        <button
                                                            onClick={() => openPermissionsPanel(user)}
                                                            disabled={permissionLoading || !operationPermissions.canManagePermissions}
                                                            className={`p-2 ${operationPermissions.canManagePermissions ? 'text-purple-500 hover:text-purple-700' : 'text-gray-300 cursor-not-allowed'}`}
                                                            title={operationPermissions.canManagePermissions ? '権限管理' : '権限管理の実行権限がありません'}
                                                        >
                                                            <LockIcon />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteUser(user.id)}
                                                            disabled={permissionLoading || !operationPermissions.canDeleteUsers}
                                                            className={`p-2 ${operationPermissions.canDeleteUsers ? 'text-red-500 hover:text-red-700' : 'text-gray-300 cursor-not-allowed'}`}
                                                            title={operationPermissions.canDeleteUsers ? '削除' : '削除権限がありません'}
                                                        >
                                                            <DeleteIcon />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* ページネーション */}
                            {totalPages > 1 && (
                                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                                    <div className="text-sm text-gray-600">
                                        全 {totalCount} 件中 {(currentPage - 1) * pageSize + 1} ～ {Math.min(currentPage * pageSize, totalCount)} 件
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => fetchUsers(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="px-3 py-2 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                                        >
                                            前へ
                                        </button>
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                            <button
                                                key={page}
                                                onClick={() => fetchUsers(page)}
                                                className={`px-3 py-2 rounded transition ${currentPage === page ? 'bg-blue-500 text-white' : 'border border-gray-300 hover:bg-gray-100'}`}
                                            >
                                                {page}
                                            </button>
                                        ))}
                                        <button
                                            onClick={() => fetchUsers(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-2 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                                        >
                                            次へ
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* 権限管理パネル */}
            {showPermissionsPanel && selectedUserForPermissions && (
                <PermissionsPanel
                    userId={selectedUserForPermissions.id}
                    userName={selectedUserForPermissions.name}
                    onClose={() => setShowPermissionsPanel(false)}
                />
            )}

            {/* ユーザー編集・作成モーダル */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-lg max-w-md w-full">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-gray-900">
                                {isEditing ? 'ユーザーを編集' : '新規ユーザーを作成'}
                            </h2>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            {error && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                                    {error}
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">名前 *</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleFormChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">ユーザーID *</label>
                                <input
                                    type="text"
                                    name="user_id"
                                    value={formData.user_id}
                                    onChange={handleFormChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">ロール</label>
                                <select
                                    name="role"
                                    value={formData.role}
                                    onChange={handleFormChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    {roleOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">ステータス</label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleFormChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    {statusOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">部署</label>
                                <input
                                    type="text"
                                    name="department"
                                    value={formData.department}
                                    onChange={handleFormChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">上司</label>
                                <select
                                    name="supervisor_id"
                                    value={formData.supervisor_id}
                                    onChange={handleFormChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="">なし</option>
                                    {supervisorOptions
                                        .filter(user => !isEditing || user.id !== selectedUser?.id)
                                        .map(user => (
                                            <option key={user.id} value={user.id}>{user.name} ({user.user_id})</option>
                                        ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">絵文字</label>
                                <input
                                    type="text"
                                    name="emoji"
                                    value={formData.emoji}
                                    onChange={handleFormChange}
                                    maxLength={16}
                                    placeholder="例: 😊"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">カラー</label>
                                <input
                                    type="text"
                                    name="color"
                                    value={formData.color}
                                    onChange={handleFormChange}
                                    placeholder="例: #3B82F6"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                                >
                                    キャンセル
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                                >
                                    {isEditing ? '更新' : '作成'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminUserManagement;
